import React from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import { defineMessages, FormattedMessage, injectIntl, useIntl } from 'react-intl';
import classNames from 'classnames';
import intlShape from '@/shared/utils/intlShape';
import RelationTypeIcon from '@/shared/components/RelationTypeIcon';
import RelationName, { getRelationName } from '@/shared/components/RelationName';
import { DeleteIcon } from '../Icons';
import SearchRelation from '../SearchRelation';
import LoadingAnimation from '../LoadingAnimation';
import Item from './select/Item';

const messages = defineMessages({
  AddRelation: 'Add relation',
  RemoveRelation: 'Remove relation',
  NoRelationsFound: 'NoRelationsFound',
  ErrorLoadingData: 'Error loading data',
  ChooseRelation: 'Choose relation',
});

const renderDropdownItems = ({
  loading,
  relations,
  error,
  highlightedIndex,
  selectedItem,
  getItemProps,
  intl,
}) => {
  if (loading) return <LoadingAnimation />;
  if (error)
    return (
      <Item item={null} getItemProps={getItemProps}>
        <FormattedMessage {...messages.ErrorLoadingData} />
      </Item>
    );
  if (relations && relations.length)
    return relations.map((relation, index) => (
      <Item
        key={relation.id}
        item={relation}
        index={index}
        isSelected={highlightedIndex === index}
        isActive={selectedItem === relation}
        getItemProps={getItemProps}
      >
        <RelationTypeIcon type={relation.type} crm={relation.crm} isActive={relation.isActive} />
        {getRelationName(intl, relation)}
      </Item>
    ));
  return (
    <Item item={null} getItemProps={getItemProps}>
      <FormattedMessage {...messages.NoRelationsFound} />
    </Item>
  );
};

class RelationListPicker extends React.Component {
  state = {
    inputValue: null,
  };

  handleStateChange = (changes, { clearSelection }) => {
    const { onAddRelation } = this.props;
    if (typeof changes.selectedItem !== 'undefined') {
      onAddRelation(changes.selectedItem);
      clearSelection();
    } else if (typeof changes.inputValue !== 'undefined') {
      this.setState({ inputValue: changes.inputValue });
    }
  };

  renderRelationInput = () => {
    const { inputValue } = this.state;
    const { relations: selectedRelations, intl, placeholder } = this.props;

    const itemToString = i => (i ? `${i.infix} ${i.name} ${i.firstName}` : '');

    return (
      <div className={classNames({ margin10B: selectedRelations && selectedRelations.length > 0 })}>
        <Downshift
          selectedItem={null}
          onStateChange={this.handleStateChange}
          itemToString={itemToString}
          defaultHighlightedIndex={0}
        >
          {({
            getInputProps,
            getMenuProps,
            isOpen,
            clearItems,
            setHighlightedIndex,
            setItemCount,
            highlightedIndex,
            getItemProps,
            selectedItem,
          }) => (
            <div>
              <input
                type="text"
                {...getInputProps({
                  placeholder: placeholder
                    ? placeholder
                    : intl.formatMessage(messages.ChooseRelation),
                  className: 'form-control ',
                })}
              />
              {!isOpen ? null : (
                <div className="Select--relative open">
                  <ul
                    {...getMenuProps({
                      className: 'Select--dropdown dropdown-menu dropdown-menu-right width100pct',
                    })}
                  >
                    <SearchRelation
                      searchValue={inputValue}
                      omitRelations={selectedRelations}
                      onLoaded={({ relations }) => {
                        clearItems();
                        if (relations) {
                          setHighlightedIndex(relations.length ? 0 : null);
                          setItemCount(relations.length);
                        }
                      }}
                    >
                      {({ loading, relations, error }) =>
                        renderDropdownItems({
                          loading,
                          relations,
                          error,
                          highlightedIndex,
                          selectedItem,
                          getItemProps,
                          intl,
                        })
                      }
                    </SearchRelation>
                  </ul>
                </div>
              )}
            </div>
          )}
        </Downshift>
      </div>
    );
  };

  renderRelationRow = relation => {
    const { disabled, intl, onRemoveRelation } = this.props;

    return (
      <div key={relation.id} className="relation-list-picker-row">
        <RelationName relation={relation} />
        {!disabled && (
          <DeleteIcon
            className="pull-right"
            onClick={() => onRemoveRelation(relation)}
            title={intl.formatMessage(messages.RemoveRelation)}
          />
        )}
      </div>
    );
  };

  render() {
    const { relations, disabled } = this.props;
    return (
      <div>
        {!disabled && this.renderRelationInput()}
        <div>{relations && relations.map(this.renderRelationRow)}</div>
      </div>
    );
  }
}

RelationListPicker.propTypes = {
  relations: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })).isRequired,

  disabled: PropTypes.bool,

  onAddRelation: PropTypes.func,
  onRemoveRelation: PropTypes.func,

  intl: intlShape.isRequired,
};

RelationListPicker.defaultProps = {
  disabled: false,
  onAddRelation: relation => {},
  onRemoveRelation: relation => {},
};
export default injectIntl(RelationListPicker);
